import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
} from '@material-ui/core'

import get from 'lodash/get'

import { Tour } from 'common/models/typings/Tour'
import { Driver } from 'common/models/typings/Driver'

import { contrastColor, getColor } from 'common/utils/Color'

import Typography from 'common/components/base/Typography'
import Icon from 'common/components/base/Icon'
import Button from 'common/components/base/Button'

import { Callout } from 'common/components/base/.raw'

export interface HandoverCartProps {
  /** for getting the cart color if not available in tour */
  index: number
  tour: Tour
  driver: Driver
  onHandover: (shipmentId: number) => void | Promise<void>
  onToggleChangeDriver: (tour: Tour) => void
}

const HandoverCart: React.FC<HandoverCartProps> = ({
  index,
  tour,
  driver,
  onHandover,
  onToggleChangeDriver,
}) => {
  const { t } = useTranslation()

  const [isPressed, setIsPressed] = React.useState(false)

  React.useEffect(() => {
    const isTKeyPressed = (event: any) => {
      setIsPressed(event.keyCode === 84)
    }
    const releaseT = (event: any) => {
      setIsPressed(false)
    }
    document.addEventListener('keydown', isTKeyPressed, false)
    document.addEventListener('keyup', releaseT, false)
    return () => {
      document.removeEventListener('keydown', isTKeyPressed, false)
      document.removeEventListener('keyup', releaseT, false)
    }
  }, [])

  const handleClick = React.useCallback(
    (collationId: number) => {
      if (isPressed) {
        onHandover(collationId)
      }
    },
    [isPressed, onHandover],
  )

  const totalDistanceInKM = React.useMemo(
    () => (get(tour, 'totalDistance') / 1000).toFixed(1).concat('km'),
    [tour],
  )

  return (
    <Card className="relative p-2">
      <CardHeader
        style={{ paddingLeft: '4px' }}
        disableTypography
        title={
          <div className="flex flex-col">
            <div
              className="flex items-center rounded-md px-2 py-1 w-fit"
              style={{
                backgroundColor: tour.color || getColor(index),
                color: contrastColor(tour.color),
              }}
            >
              <div className="font-bold mr-6">
                {t('CART')} {tour.number}
              </div>
              <Box className="h-4 w-6 flex items-center justify-center bg-white rounded-2xl">
                <Typography variant="badge" color="indigo">
                  {tour.collations.length}
                </Typography>
              </Box>
              <div className="absolute top-4 right-4">
                <Button
                  color="white"
                  onClick={() => onToggleChangeDriver(tour)}
                  className="p-2 min-w-[32px] h-8 shadow-none"
                >
                  <Icon name="edit-user" style={{ height: 16, width: 16 }} />
                </Button>
              </div>
            </div>
          </div>
        }
        className="pb-2"
      />
      <div className="px-2 py-1">
        <Typography noWrap variant="textBold">
          Driver: {driver?.name}
        </Typography>
        <Typography variant="badge" color="indigo">
          {t('TOTAL_DISTANCE')}: {totalDistanceInKM}
        </Typography>
        <Typography variant="tableHeader">Parcels</Typography>
        <div className="w-full overflow-y-auto max-h-[172px] p-0">
          {!tour.collations.length && (
            <Callout variant="success" className="min-w-full">
              {t('ALL_SHIPMENTS_HANDOVERED')}
            </Callout>
          )}

          <List dense className="p-0">
            {tour.collations &&
              tour.collations.map((collation, cIndex) => (
                <ListItem
                  button
                  dense
                  disableGutters
                  onClick={() => handleClick(Number(collation.id))}
                  style={{
                    borderBottom:
                      cIndex < tour.collations.length - 1
                        ? '1px solid rgba(0, 0, 0, .12)'
                        : 'none',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography className="text-xs">
                        {collation.recipient.location.address}
                      </Typography>
                    }
                    secondary={
                      <Typography className="text-xs">
                        {collation.recipient.location.postalCode}{' '}
                        {collation.recipient.location.city}
                      </Typography>
                    }
                  />
                  {collation.isWorking && (
                    <ListItemSecondaryAction>
                      <Tooltip
                        arrow
                        placement="right"
                        title={<>{t('PROCESSING')}</>}
                        classes={{ tooltip: 'text-xs' }}
                      >
                        <CircularProgress size={24} />
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
          </List>
        </div>
      </div>
    </Card>
  )
}

export default HandoverCart
